import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * /workspace/worlds — inspector for SONMI's domain understanding.
 *
 * A World is a named domain (e.g., "payroll"). Concepts within a world
 * pass through phases: encountered → noticed → provisional → established
 * (contested / retired are also possible). Every concept carries its
 * own history: observations from artifacts, and phase transitions.
 *
 * This page exists to watch a world grow from observation into
 * understanding. It is a research surface, not a product interface.
 */

const PHASE_ORDER = ["established", "provisional", "noticed", "encountered", "contested", "retired"];
const PHASE_COLOR = {
  encountered: "rgba(255,255,255,0.4)",
  noticed: "rgba(180,220,255,0.75)",
  provisional: "rgba(150,240,180,0.75)",
  established: "rgba(255,220,140,0.85)",
  contested: "rgba(255,160,160,0.75)",
  retired: "rgba(120,120,130,0.5)",
};

export { PHASE_ORDER, PHASE_COLOR };

export default function WorldsPage() {
  const [worlds, setWorlds] = useState([]);
  const [worldId, setWorldId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pending, setPending] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(true);

  const refreshWorlds = useCallback(async () => {
    const r = await api.get("/worlds/");
    setWorlds(r.data);
    if (r.data.length && !worldId) setWorldId(r.data[0].id);
    setLoading(false);
  }, [worldId]);

  useEffect(() => { refreshWorlds(); }, [refreshWorlds]);

  const loadDetail = useCallback(async (id) => {
    const [w, p] = await Promise.all([
      api.get(`/worlds/${id}`),
      api.get(`/worlds/${id}/pending`),
    ]);
    setDetail(w.data);
    setPending(p.data);
  }, []);

  useEffect(() => {
    if (!worldId) { setDetail(null); setPending([]); return; }
    loadDetail(worldId);
  }, [worldId, loadDetail]);

  const createWorld = async () => {
    if (!newName.trim()) return;
    await api.post("/worlds/", { name: newName.trim() });
    setNewName("");
    refreshWorlds();
  };

  const approveCandidate = async (cid, reason) => {
    await api.post(`/worlds/${worldId}/pending/${cid}/approve`, reason ? { reason } : {});
    loadDetail(worldId);
  };
  const rejectCandidate = async (cid, reason) => {
    await api.post(`/worlds/${worldId}/pending/${cid}/reject`, reason ? { reason } : {});
    loadDetail(worldId);
  };

  const conceptsByPhase = {};
  if (detail?.concepts) {
    for (const p of PHASE_ORDER) conceptsByPhase[p] = [];
    for (const c of detail.concepts) (conceptsByPhase[c.phase] || (conceptsByPhase[c.phase] = [])).push(c);
  }

  return (
    <div data-testid="worlds-page" style={{
      height: "100vh", background: "#050508", color: "rgba(255,255,255,0.88)",
      fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace", fontSize: 12,
      display: "grid", gridTemplateColumns: "260px 1fr", overflow: "hidden",
    }}>
      {/* Left: worlds list */}
      <div style={{ borderRight: "1px solid rgba(255,255,255,0.1)", padding: 16, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.55 }}>SONMI · Worlds</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            data-testid="worlds-new-name"
            placeholder="new world"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createWorld()}
            style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)", color: "inherit", padding: "6px 8px", fontSize: 11, fontFamily: "inherit", borderRadius: 3, outline: "none" }}
          />
          <button data-testid="worlds-create" onClick={createWorld} style={btnSmall}>+</button>
        </div>
        {loading && <div style={emptyStyle}>loading…</div>}
        {worlds.map((w) => (
          <div key={w.id} onClick={() => setWorldId(w.id)}
               data-testid={`world-${w.id}`}
               style={{ padding: "8px 10px", borderRadius: 4, cursor: "pointer",
                        background: w.id === worldId ? "rgba(255,255,255,0.06)" : "transparent",
                        border: `1px solid ${w.id === worldId ? "rgba(255,255,255,0.16)" : "transparent"}` }}>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.92)" }}>{w.name}</div>
            {w.description && <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4 }}>{w.description}</div>}
          </div>
        ))}
        <div style={{ marginTop: "auto", fontSize: 10, opacity: 0.4 }}>
          <a href="/workspace/debug" style={{ color: "inherit", textDecoration: "none" }}>← debug</a>
          {"  ·  "}
          <a href="/workspace" style={{ color: "inherit", textDecoration: "none" }}>workspace</a>
        </div>
      </div>

      {/* Right: world detail */}
      <div style={{ padding: 16, overflowY: "auto" }}>
        {!detail && <div style={emptyStyle}>select a world</div>}
        {detail && (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300 }}>{detail.world.name}</div>
              {detail.world.description && <div style={{ fontSize: 11, opacity: 0.55, marginTop: 4 }}>{detail.world.description}</div>}
              <div style={{ fontSize: 10, opacity: 0.4, marginTop: 6, letterSpacing: "0.06em" }}>
                {detail.concepts.length} concepts · sealed {new Date(detail.world.created_at).toLocaleString()}
              </div>
              <div style={{ fontSize: 11, opacity: 0.6, marginTop: 10, fontFamily: "'Inter', sans-serif", fontStyle: "italic" }}>
                To seed this world, POST an artifact into it: <code>POST /api/worlds/{detail.world.id}/observe</code> with{" "}
                <code>&#123; session_id, artifact_id &#125;</code>
                <br/>
                Nothing enters the world's understanding until you review the candidates below.
              </div>
            </div>

            {pending.length > 0 && (
              <div data-testid="pending-review" style={{ marginBottom: 28, padding: 14, border: "1px solid rgba(255,220,140,0.28)", borderRadius: 6, background: "rgba(255,220,140,0.03)" }}>
                <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,220,140,0.85)", marginBottom: 10, display: "flex", justifyContent: "space-between" }}>
                  <span>pending review · {pending.length} candidate{pending.length === 1 ? "" : "s"}</span>
                  <span style={{ opacity: 0.55, letterSpacing: "0.04em", textTransform: "none" }}>governance: nothing enters the world until you approve</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {pending.map((c) => (
                    <PendingCandidate key={c.id} c={c}
                      onApprove={(r) => approveCandidate(c.id, r)}
                      onReject={(r) => rejectCandidate(c.id, r)} />
                  ))}
                </div>
              </div>
            )}

            {PHASE_ORDER.filter((p) => (conceptsByPhase[p] || []).length > 0).map((phase) => (
              <div key={phase} style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
                              color: PHASE_COLOR[phase], marginBottom: 8, display: "flex", gap: 8, alignItems: "baseline" }}>
                  <span>{phase}</span>
                  <span style={{ opacity: 0.5, fontSize: 9 }}>({(conceptsByPhase[phase] || []).length})</span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(conceptsByPhase[phase] || []).map((c) => (
                    <div key={c.id}
                         data-testid={`concept-${c.id}`}
                         onClick={() => setSelectedConcept(c.id)}
                         style={{
                           padding: "6px 10px",
                           border: `1px solid ${PHASE_COLOR[phase]}`,
                           borderRadius: 12,
                           background: phase === "encountered" ? "transparent" : `${PHASE_COLOR[phase].replace(/[\d.]+\)$/, "0.08)")}`,
                           fontSize: 11,
                           cursor: "pointer",
                           color: "rgba(255,255,255,0.9)",
                         }}>
                      {c.name}
                      <span style={{ opacity: 0.5, marginLeft: 6, fontSize: 9 }}>
                        {c.observation_count}/{c.source_artifact_count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {detail.concepts.length === 0 && (
              <div style={emptyStyle}>
                This world has no concepts yet. Observe an artifact into it and watch understanding accrete.
              </div>
            )}
          </>
        )}
      </div>

      {selectedConcept && detail && (
        <ConceptDetail worldId={worldId} conceptId={selectedConcept} onClose={() => setSelectedConcept(null)} />
      )}
    </div>
  );
}

/* ---------- concept detail overlay ---------- */
export function ConceptDetail({ worldId, conceptId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(`/worlds/${worldId}/concepts/${conceptId}`).then((r) => setD(r.data));
  }, [worldId, conceptId]);

  const retire = async () => {
    if (!confirm("Retire this concept? It will be marked as no longer part of the world.")) return;
    await api.post(`/worlds/${worldId}/concepts/${conceptId}/retire`, { reason: "Retired via debug UI." });
    onClose();
  };

  return (
    <div data-testid="concept-detail" onClick={onClose}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "#0c0c14", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6,
                    padding: 24, width: "min(760px, 94vw)", maxHeight: "88vh", overflowY: "auto" }}>
        {!d && <div style={emptyStyle}>loading…</div>}
        {d && (
          <>
            <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.55, marginBottom: 10 }}>
              concept · {d.concept.phase}
            </div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: PHASE_COLOR[d.concept.phase] }}>
              {d.concept.name}
            </div>
            {d.concept.aliases && d.concept.aliases.length > 0 && (
              <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>
                aliases: {d.concept.aliases.map((a) => `"${a}"`).join(", ")}
              </div>
            )}
            {d.concept.provisional_definition && (
              <div style={{ fontFamily: "'Inter', sans-serif", fontStyle: "italic",
                            fontSize: 13, marginTop: 12, color: "rgba(255,255,255,0.82)", lineHeight: 1.5 }}>
                "{d.concept.provisional_definition}"
              </div>
            )}
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.5, marginBottom: 6 }}>
                history of becoming
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, borderLeft: "2px solid rgba(255,255,255,0.12)", paddingLeft: 12 }}>
                {d.phase_events.map((e) => (
                  <div key={e.id} style={{ fontSize: 11, color: "rgba(255,255,255,0.78)", lineHeight: 1.5 }}>
                    <span style={{ color: PHASE_COLOR[e.to_phase] }}>{e.from_phase || "∅"} → {e.to_phase}</span>
                    <span style={{ opacity: 0.5 }}>  ·  {new Date(e.at).toLocaleString()}</span>
                    <div style={{ opacity: 0.6, fontSize: 10, marginTop: 2 }}>{e.reason}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", opacity: 0.5, marginBottom: 6 }}>
                observations ({d.observations.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {d.observations.map((o) => (
                  <div key={o.id} style={{ padding: "8px 10px", background: "rgba(255,255,255,0.03)", borderLeft: "2px solid rgba(255,255,255,0.14)", fontSize: 11 }}>
                    <div style={{ opacity: 0.85, whiteSpace: "pre-wrap" }}>"{o.excerpt}"</div>
                    <div style={{ opacity: 0.4, fontSize: 9, marginTop: 4, letterSpacing: "0.06em" }}>
                      artifact {o.artifact_id.slice(0, 8)} · conf {o.confidence?.toFixed(2)} · {new Date(o.at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {d.contradictions && d.contradictions.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(255,160,160,0.85)", marginBottom: 6 }}>
                  contradictions ({d.contradictions.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {d.contradictions.map((o) => (
                    <div key={o.id} style={{ padding: "8px 10px", background: "rgba(255,140,140,0.05)", borderLeft: "2px solid rgba(255,160,160,0.6)", fontSize: 11 }}>
                      <div style={{ opacity: 0.85, whiteSpace: "pre-wrap" }}>"{o.excerpt}"</div>
                      {o.contradiction_reason && (
                        <div style={{ fontStyle: "italic", opacity: 0.75, marginTop: 4, fontFamily: "'Inter', sans-serif" }}>
                          — {o.contradiction_reason}
                        </div>
                      )}
                      <div style={{ opacity: 0.4, fontSize: 9, marginTop: 4, letterSpacing: "0.06em" }}>
                        artifact {o.artifact_id.slice(0, 8)} · {o.proposed_by} · {new Date(o.at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {d.concept.phase !== "retired" && (
                <button data-testid="concept-retire" onClick={retire} style={{ ...btnSmall, borderColor: "rgba(255,160,160,0.4)", color: "rgba(255,180,180,0.85)" }}>
                  retire concept
                </button>
              )}
              <button data-testid="concept-close" onClick={onClose} style={btnSmall}>close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const btnSmall = {
  background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.75)",
  border: "1px solid rgba(255,255,255,0.14)", padding: "6px 12px",
  borderRadius: 3, fontSize: 11, cursor: "pointer", fontFamily: "inherit",
};
const emptyStyle = { fontSize: 11, opacity: 0.4, fontStyle: "italic" };

export { btnSmall, emptyStyle };

/* ---------- pending candidate row ---------- */
export function PendingCandidate({ c, onApprove, onReject }) {
  const [showReason, setShowReason] = useState(false);
  const [reason, setReason] = useState("");
  const [showContext, setShowContext] = useState(false);
  const reinforces = !!c.would_reinforce_concept_name;
  const hasContext = c.excerpt_context && (c.excerpt_context.before || c.excerpt_context.after);
  return (
    <div data-testid={`pending-${c.id}`} style={{
      padding: "10px 12px",
      background: "rgba(255,255,255,0.02)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderLeft: `3px solid ${reinforces ? "rgba(150,240,180,0.6)" : "rgba(255,220,140,0.6)"}`,
      borderRadius: 4,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.94)" }}>
          {c.name}
          <span style={{ marginLeft: 8, opacity: 0.5, fontSize: 10 }}>{c.kind}</span>
          {reinforces && (
            <span style={{ marginLeft: 10, fontSize: 9, letterSpacing: "0.12em", color: "rgba(150,240,180,0.9)" }}>
              WOULD STRENGTHEN · {c.would_reinforce_concept_name}
            </span>
          )}
          {!reinforces && (
            <span style={{ marginLeft: 10, fontSize: 9, letterSpacing: "0.12em", color: "rgba(255,220,140,0.9)" }}>
              WOULD ENTER AS NEW
            </span>
          )}
        </div>
        <div style={{ fontSize: 9, opacity: 0.5 }}>conf {(c.confidence || 0).toFixed(2)}</div>
      </div>
      {c.aliases && c.aliases.length > 0 && (
        <div style={{ marginTop: 4, fontSize: 10, opacity: 0.55, fontFamily: "'Inter', sans-serif" }}>
          also: {c.aliases.join(" · ")}
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 11, opacity: 0.75, fontFamily: "'Inter', sans-serif", fontStyle: "italic" }}>
        "{c.excerpt}"
      </div>
      {c.reasoning && (
        <div style={{
          marginTop: 6,
          fontSize: 11,
          opacity: 0.78,
          fontFamily: "'Inter', sans-serif",
          borderLeft: "2px solid rgba(180,200,240,0.35)",
          paddingLeft: 8,
          color: "rgba(220,230,255,0.85)",
        }}>
          <span style={{ fontSize: 9, letterSpacing: "0.12em", opacity: 0.6, marginRight: 6 }}>WHY</span>
          {c.reasoning}
        </div>
      )}
      {c.provisional_definition && (
        <div style={{ marginTop: 4, fontSize: 11, opacity: 0.6, fontFamily: "'Inter', sans-serif" }}>
          proposed definition: {c.provisional_definition}
        </div>
      )}
      {hasContext && (
        <div style={{ marginTop: 6 }}>
          <button
            data-testid={`pending-${c.id}-context-toggle`}
            onClick={() => setShowContext((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(180,200,240,0.7)",
              fontFamily: "inherit",
              fontSize: 10,
              letterSpacing: "0.08em",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {showContext ? "hide surrounding text" : "show surrounding text"}
          </button>
          {showContext && (
            <div
              data-testid={`pending-${c.id}-context`}
              style={{
                marginTop: 6,
                padding: "8px 10px",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 3,
                fontFamily: "'Inter', sans-serif",
                fontSize: 11,
                lineHeight: 1.55,
                opacity: 0.85,
                whiteSpace: "pre-wrap",
              }}
            >
              {c.excerpt_context.truncated_before && (
                <span style={{ opacity: 0.4 }}>… </span>
              )}
              <span style={{ opacity: 0.55 }}>{c.excerpt_context.before}</span>
              <span style={{
                background: "rgba(255,220,140,0.18)",
                color: "rgba(255,235,180,0.98)",
                padding: "0 2px",
                borderRadius: 2,
              }}>{c.excerpt_context.match}</span>
              <span style={{ opacity: 0.55 }}>{c.excerpt_context.after}</span>
              {c.excerpt_context.truncated_after && (
                <span style={{ opacity: 0.4 }}> …</span>
              )}
            </div>
          )}
        </div>
      )}
      <div style={{ marginTop: 4, fontSize: 9, opacity: 0.4, letterSpacing: "0.06em" }}>
        from artifact "{c.artifact_title}" · {new Date(c.created_at).toLocaleString()}
      </div>
      {showReason && (
        <input
          data-testid={`pending-${c.id}-reason`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="reason (optional)"
          style={{
            marginTop: 8, width: "100%", background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.14)", color: "inherit",
            padding: "5px 8px", fontSize: 11, fontFamily: "inherit", borderRadius: 3, outline: "none", boxSizing: "border-box",
          }}
        />
      )}
      <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button
          data-testid={`pending-${c.id}-approve`}
          onClick={() => onApprove(reason || null)}
          title="Enter this into the World as shared understanding"
          style={{ ...btnSmall, borderColor: "rgba(150,240,180,0.4)", color: "rgba(180,240,200,0.95)" }}>
          establish shared understanding
        </button>
        <button
          data-testid={`pending-${c.id}-reject`}
          onClick={() => onReject(reason || null)}
          title="Keep this in the record but do not adopt it into the World"
          style={{ ...btnSmall, borderColor: "rgba(255,160,160,0.4)", color: "rgba(255,180,180,0.9)" }}>
          hold — not adopted
        </button>
        <button onClick={() => setShowReason((v) => !v)} style={{ ...btnSmall, opacity: 0.7 }}>
          {showReason ? "hide reason" : "+ reason"}
        </button>
      </div>
    </div>
  );
}
